import * as React from "react";
import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Typography from "@mui/material/Typography";
import EditFranchiseDetailsDialog from "./dialogs/EditFranchiseDetailsDialog";
import EditDefaultOwnershipDialog from "./dialogs/EditDefaultOwnershipDialog";

// The franchise as a visually distinct BAND, not a table row (design §3 rule 1). This is the
// structural fix for the classic screen, where the franchise lived in a <TableHead> sharing an
// 8-column grid with facility rows that meant something completely different in each column.
//
// Deliberately absent:
//  - an eligibility chip (decision #14 -- it would need a second fetch and would force
//    reconciliation of two different franchise universes);
//  - franchise notes and the warehouse email, which belong in the Edit Franchise Details dialog;
//  - any editing control. Default BDM stays READ-ONLY until the Track B bdm_id migration lands
//    (design §11.5) -- we are explicitly not shipping a free-text editor we intend to replace.
export default function FranchiseSummaryBand(props) {
  const franchise = props.franchise;
  const expanded = props.expanded;
  const onToggle = props.onToggle;
  const onSaveFranchiseDetails = props.onSaveFranchiseDetails;
  const onSaveFranchiseOwnership = props.onSaveFranchiseOwnership;
  const bdmOptions = props.bdmOptions;
  const onSaved = props.onSaved;

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [ownershipOpen, setOwnershipOpen] = useState(false);

  const attentionCount = franchise.attentionCount;

  // The RESOLVED default -- primary plus "+N" -- rather than the raw legacy text, so the band shows
  // what facilities actually inherit.
  const ownership = franchise.ownership || {};
  const defaultBdmLabel = ownership.primaryName || franchise.defaultBdmName || "—";
  const additionalOwners = ownership.additionalCount > 0 ? ` +${ownership.additionalCount}` : "";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        px: 1.5,
        py: 1.25,
        bgcolor: "action.hover",
        borderBottom: expanded ? 1 : 0,
        borderColor: "divider",
      }}
    >
      <IconButton
        size="small"
        onClick={onToggle}
        aria-label={expanded ? `Collapse ${franchise.name}` : `Expand ${franchise.name}`}
      >
        {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {franchise.name}
          </Typography>
          {franchise.isActive ? (
            <Chip label="Active" size="small" color="success" variant="outlined" />
          ) : (
            <Chip
              label="Inactive"
              size="small"
              variant="outlined"
              sx={{ color: "text.secondary", borderColor: "divider" }}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            {franchise.comcode}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Default BDM: <strong>{defaultBdmLabel}{additionalOwners}</strong>
          {" · "}
          {franchise.facilityCount} {franchise.facilityCount === 1 ? "Facility" : "Facilities"}
        </Typography>

        {attentionCount > 0 && (
          <Typography variant="body2" color="warning.main">
            ⚠ {attentionCount} {attentionCount === 1 ? "facility needs" : "facilities need"} attention
          </Typography>
        )}
      </Box>

      {/* Franchise-level actions. Edit Default Ownership arrived with Multi-BDM M1, which made the
          franchise default an editable ID-backed collection. */}
      <Box sx={{ flex: "0 0 auto", pt: 0.25, display: "flex", gap: 0.5 }}>
        {onSaveFranchiseOwnership && (
          <Button
            size="small"
            onClick={() => setOwnershipOpen(true)}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            Edit Default Ownership
          </Button>
        )}
        {onSaveFranchiseDetails && (
          <Button
            size="small"
            onClick={() => setDetailsOpen(true)}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            Edit Franchise Details
          </Button>
        )}
      </Box>

      {onSaveFranchiseDetails && (
        <EditFranchiseDetailsDialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          franchise={franchise}
          onSave={onSaveFranchiseDetails}
          onSaved={onSaved}
        />
      )}

      {onSaveFranchiseOwnership && (
        <EditDefaultOwnershipDialog
          open={ownershipOpen}
          onClose={() => setOwnershipOpen(false)}
          franchise={franchise}
          bdmOptions={bdmOptions}
          onSave={onSaveFranchiseOwnership}
          onSaved={onSaved}
        />
      )}
    </Box>
  );
}
